import { Moment } from 'moment'
import moment from 'moment/moment'
import { useCallback, useEffect, useState } from 'react'
import { Modal } from '@material-ui/core'
import { Goal } from 'Generated/graphql-types'
import { Button } from '@resiliatech/birdsong'
import {
  Buttons,
  Content,
  DateRangeFilters,
  EditChartModalContainer,
  Header,
  HeaderText,
} from './EditDataModal.styled'
import { EditChartData } from '../EditChartData/EditChartData'
import { DatePicker } from '../../../../SharedComponents/DatePicker/DatePicker'
import { EditDataConfirmationModal } from '../EditDataConfirmationModal/EditDataConfirmationModal'

type EditDataModalProps = {
  goal: Goal
  isOpen: boolean
  onClose: () => void
}

export const EditDataModal = ({
  goal,
  isOpen,
  onClose,
}: EditDataModalProps) => {
  const groups = goal.EntryGroup.filter((e) => !e.archivedAt).sort((a, b) =>
    moment.utc(b.reportedAt).diff(moment.utc(a.reportedAt))
  )
  const [filterStartDate, setFilterStartDate] = useState(
    groups.at(-1)?.reportedAt
  )
  const [filterEndDate, setFilterEndDate] = useState(groups[0]?.reportedAt)
  const [isChartDataEdited, setIsChartDataEdited] = useState(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isDataFilterOpen, setIsDataFilterOpen] = useState(false)

  const resetFilterDates = useCallback(() => {
    setFilterStartDate(groups.at(-1)?.reportedAt)
    setFilterEndDate(groups[0]?.reportedAt)
  }, [groups])

  useEffect(() => {
    if (isOpen) {
      resetFilterDates()
    }
  }, [isOpen, onClose, resetFilterDates])

  const handleFilterDates = (date: Moment | null, type: 'start' | 'end') => {
    if (type === 'start') setFilterStartDate(date ? moment(date) : null)
    if (type === 'end') setFilterEndDate(date ? moment(date) : null)
  }

  const datePickerStyles = {
    maxWidth: '200px',
  }

  const filteredEntryGroups = groups.filter((group) => {
    if (filterStartDate && filterEndDate) {
      return (
        moment.utc(group.reportedAt).isSameOrAfter(filterStartDate) &&
        moment.utc(group.reportedAt).isSameOrBefore(filterEndDate)
      )
    }
    if (filterStartDate)
      return moment.utc(group.reportedAt).isSameOrAfter(filterStartDate)
    if (!filterStartDate && filterEndDate)
      return moment.utc(group.reportedAt).isSameOrBefore(filterEndDate)
    return true
  })

  const handleClose = () => {
    if (isChartDataEdited) {
      handleOpenConfirmationModal()
    } else {
      resetFilterDates()
      onClose()
    }
  }

  const handleOpenConfirmationModal = () => {
    setIsConfirmationModalOpen(true)
  }

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false)
  }
  const handleCloseAll = () => {
    onClose()
    setIsConfirmationModalOpen(false)
    setIsChartDataEdited(false)
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        disableEnforceFocus
      >
        <EditChartModalContainer>
          <Content>
            <Header>
              <HeaderText>Edit Chart Data</HeaderText>
              {isDataFilterOpen && (
                <DateRangeFilters>
                  <DatePicker
                    date={filterStartDate}
                    label='Start Date'
                    onDateSelected={(startDate) =>
                      handleFilterDates(startDate, 'start')
                    }
                    styles={datePickerStyles}
                    props={{ minDate: moment(groups.at(-1)?.reportedAt) }}
                  />
                  <DatePicker
                    date={filterEndDate}
                    label='End Date'
                    onDateSelected={(endDate) =>
                      handleFilterDates(endDate, 'end')
                    }
                    styles={datePickerStyles}
                    props={{ maxDate: moment(groups[0]?.reportedAt) }}
                  />
                </DateRangeFilters>
              )}
            </Header>
            <EditChartData
              entryGroups={filteredEntryGroups}
              goalId={goal.id.toString()}
              isDataFilterOpen={isDataFilterOpen}
              setIsChartDataEdited={setIsChartDataEdited}
              setIsDataFilterOpen={setIsDataFilterOpen}
            />
          </Content>
          <Buttons>
            <Button
              label='Close'
              onClick={handleClose}
              type='button'
              variant='primary'
            />
          </Buttons>
        </EditChartModalContainer>
      </Modal>
      <EditDataConfirmationModal
        onCloseConfirmation={handleCloseConfirmationModal}
        isConfirmationModalOpen={isConfirmationModalOpen}
        onCloseAll={handleCloseAll}
      />
    </>
  )
}
